/** @format */

import { Component, ViewChild, OnDestroy } from "@angular/core";
import { Router, RouterOutlet, NavigationEnd } from "@angular/router";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { Roles } from "src/app/auth/roles";
import { RefreshStateService } from "src/app/services/refresh-state/refresh-state.service";

@Component({
  standalone: false,
  selector: "app-settings",
  templateUrl: "./settings.component.html",
  styleUrls: ["./settings.component.css"],
})
export class SettingsComponent implements OnDestroy {
  @ViewChild(RouterOutlet) outlet!: RouterOutlet;
  title: string = "Settings";
  breadcrumbs: { label: string; url: string }[] = [];
  isMenuCollapsed = true;

  private routerSub!: Subscription;
  private readonly menuResetSubscription = new Subscription();

  formatLabel(label: string): string {
    return label
      .split("-")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private routeLabels: { [key: string]: string } = {
    'tenant': 'Account Holder',
    'add-tenant': 'Add Account Holder',
    'edit-tenant': 'Edit Account Holder',
    'subsidiaries': 'Client',
    'add-subsidiary': 'Add Client',
    'edit-subsidiary': 'Edit Client',
    'branch': 'Branch',
    'add-branch': 'Add Branch',
    'edit-branch': 'Edit Branch',
    'form-fields': 'Form Fields',
    'add-form-field': 'Add Form Field',
    'edit-form-field': 'Edit Form Field',
    'configure-notifications': 'Configure Notifications',
    'forms': 'Forms',
    'add-form': 'Add Form',
    'schedule-form': 'Schedule Form',
    'edit-form': 'Edit Form',
    'assign-form': 'Assign Form',
    'groups': 'Groups',
    'add-group': 'Add Group',
    'modification-window': 'Modification Window',
    'create-workflow': 'Create Workflow',
    'build': 'Build',
    'user': 'User',
    'add-super-user': 'Add Super User',
    'add-admin-user': 'Add Admin User',
    'add-non-admin-user': 'Add Non-Admin User',
    'edit-non-admin-user': 'Edit Non-Admin User',
    'edit-admin-user': 'Edit Admin User',
    'profile': 'Profile',
    'audit': 'Audit',
    'add-temp-group': 'Add Temp Group',
    'add-temp-user': 'Add Temp User',
    'edit-temporary-user': 'Edit Temp User',
    'assign-temp-form': 'Temp Assign Form',
    'api-connections': 'API Endpoints',
    'cloud-connections': 'Cloud Connections',
    'integrations': 'Integrations',
    'workflows': 'Workflows'
  };

  constructor(
    private router: Router,
    private refreshState: RefreshStateService,
  ) { }

  settingsMenus = [
    {
      subMenuTitle: "Account Holder",
      subMenuIcon: "../../../assets/company.svg",
      subMenuDesc: "Add and remove account holders from Smart Forms",
      subMenuLink: "tenant",
    },
    {
      subMenuTitle: "Client",
      subMenuIcon: "../../../assets/plant.svg",
      subMenuDesc: "Add and remove clients from Account Holders.",
      subMenuLink: "subsidiaries",
    },
    {
      subMenuTitle: "Branch",
      subMenuIcon: "../../../assets/unit.svg",
      subMenuDesc: "Add and remove branches from clients.",
      subMenuLink: "branch",
    },
    {
      subMenuTitle: "Form Field",
      subMenuIcon: "../../../assets/datapoint.svg",
      subMenuDesc:
        "Add or remove form fields from branches. These are individual fields that will be populated on data collection for a Branch.",
      subMenuLink: "form-fields",
    },
    {
      subMenuTitle: "Forms",
      subMenuIcon: "../../../assets/logsheet.svg",
      subMenuDesc:
        "Configure form templates with existing form fields per Branch. Schedule and delete previously deleted form templates.",
      subMenuLink: "forms",
    },
    {
      subMenuTitle: "Group",
      subMenuIcon: "../../../assets/group.svg",
      subMenuDesc:
        "Create Groups to which Users and Form Templates can get assigned.",
      subMenuLink: "groups",
    },
    {
      subMenuTitle: "Modification Window",
      subMenuIcon: "../../../assets/edit-pencil.svg",
      subMenuDesc:
        "Edit the period of time which data other roles may edit the data captured.",
      subMenuLink: "modification-window",
    },
    {
      subMenuTitle: "User",
      subMenuIcon: "../../../assets/user-icon.svg",
      subMenuDesc:
        "Create user and grant either non-admin, admin or super admin permissions.",
      subMenuLink: "user",
    },

    {
      subMenuTitle:"Integrations",
      subMenuIcon:"../../../assets/integrations-icon.svg",
      subMenuDesc:"Connect your forms to other apps.",
      subMenuLink:"integrations",

    },
    {
      subMenuTitle: "Workflows",
      subMenuIcon: "../../../assets/workflow.svg",
      subMenuDesc:
        "Build multi-stage approval workflows with drag-and-drop fields, routing, and validation.",
      subMenuLink: "workflows",
    },
    {
      subMenuTitle: "Profile",
      subMenuIcon: "../../../assets/profile-icon-small.svg",
      subMenuDesc: "Edit Profile Details",
      subMenuLink: "profile",
    },
    {
      subMenuTitle: "Audit",
      subMenuIcon: "../../../assets/audit.svg",
      subMenuDesc: "View Audit table and Details",
      subMenuLink: "audit",
    },
  ];

  private buildBreadcrumbs(): void {
    const path = this.router.url.split('?')[0];
    const segments = path.split('/').filter(s => s);
    const settingsIdx = segments.indexOf('settings');

    if (settingsIdx === -1) {
      this.breadcrumbs = [];
      return;
    }

    const afterSettings = segments.slice(settingsIdx + 1);
    const crumbs: { label: string; url: string }[] = [];
    let currentPath = '/settings';

    for (const seg of afterSettings) {
      currentPath += '/' + seg;
      if (/^\d+$/.test(seg) || /^[0-9a-f]{8}-[0-9a-f]{4}/i.test(seg)) {
        continue;
      }
      crumbs.push({ label: this.routeLabels[seg] ?? seg, url: currentPath });
    }

    this.breadcrumbs = crumbs;
    this.title = crumbs.length > 0 ? crumbs[crumbs.length - 1].label : 'Settings';
  }

  goBack() {
    window.history.back();
  }

  ngOnInit(): void {
    this.menuResetSubscription.add(
      this.refreshState.areaReset$.subscribe(area => {
        if (area === "settings") this.isMenuCollapsed = true;
      }),
    );
    const saved = this.refreshState.load<{ isMenuCollapsed: boolean }>("settings-shell");
    if (saved) this.isMenuCollapsed = saved.isMenuCollapsed;

    const currentRole = localStorage.getItem("role");
    if (currentRole === Roles.admin) {
      let clonedSettings = [...this.settingsMenus];
      const result = clonedSettings.filter((item) => {
        if (
          item.subMenuTitle !== "Account Holder" &&
          item.subMenuTitle !== "Client" &&
          item.subMenuTitle !== "Audit" &&
          item.subMenuTitle !== "Workflows"
        ) {
          return item;
        }
        return null;
      });
      this.settingsMenus = result;
    } else if (currentRole === Roles.superAdmin) {
      let clonedSettings = [...this.settingsMenus];
      const result = clonedSettings.filter((item) => {
        if (item.subMenuTitle !== "Integrations") {
          return item;
        }
        return null;
      });
      this.settingsMenus = result;
    }

    if (currentRole === Roles.operator) {
      this.settingsMenus = this.settingsMenus.filter(menu => menu.subMenuTitle === "Profile");
    }

    this.buildBreadcrumbs();

    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.buildBreadcrumbs());
  }

  toggleMenuCollapse(): void {
    this.isMenuCollapsed = !this.isMenuCollapsed;
    this.persistRefreshState();
  }

  ngOnDestroy(): void {
    this.persistRefreshState();
    this.routerSub?.unsubscribe();
    this.menuResetSubscription.unsubscribe();
  }

  private persistRefreshState(): void {
    this.refreshState.save("settings-shell", { isMenuCollapsed: this.isMenuCollapsed });
  }
}
